/**
 * Checks if a string looks like a bare domain name (e.g., google.com, sub.example.org)
 * without a protocol prefix.
 */
const isBareDomain = (href) => {
    // Pattern: starts with alphanumeric, contains a dot, followed by a valid TLD pattern
    // Examples: google.com, sub.example.org, my-site.co.uk
    // Does NOT match: ./path, ../path, path/to/ID, /path
    const bareDomainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+([:/]|$)/;
    return bareDomainPattern.test(href);
};
/**
 * Normalizes a URL by converting backslashes to forward slashes and
 * collapsing multiple leading slashes to prevent open redirect vulnerabilities.
 *
 * For apps using hash-based routing (e.g., `domain.com/#/path`), relative paths
 * starting with `/` are converted to hash-based paths starting with `#/`.
 *
 * Bare domain names (e.g., `google.com`) are treated as external URLs and
 * prefixed with `https://`.
 *
 * Examples:
 * - `/\google.com` → `#/google.com` (prevents open redirect, uses hash routing)
 * - `//google.com` → `#/google.com` (prevents protocol-relative redirect)
 * - `/\/\/\/google.com` → `#/google.com` (normalizes multiple slashes)
 * - `/path/to/page` → `#/path/to/page` (in-app hash routing)
 * - `google.com` → `https://google.com` (bare domain → external URL)
 * - `example.org/path` → `https://example.org/path` (bare domain with path)
 * - `https://example.com` → `https://example.com` (external URLs unchanged)
 * - `#/existing-hash` → `#/existing-hash` (already hash-based, unchanged)
 */
export const sanitizeHrefUrl = (href) => {
    if (!href) {
        return href;
    }
    // Check for dangerous URL schemes first - these should be removed entirely
    // eslint-disable-next-line no-script-url
    const dangerousSchemes = ['javascript:', 'data:', 'vbscript:'];
    const lowerHref = href.toLowerCase().trim();
    if (dangerousSchemes.some((scheme) => lowerHref.startsWith(scheme))) {
        return null; // Signal to remove the href
    }
    // If it's already a hash-based path, external URL, or protocol-based URL, return as-is
    if (href.startsWith('#') ||
        href.startsWith('http://') ||
        href.startsWith('https://') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')) {
        return href;
    }
    // Check if it looks like a bare domain name (e.g., google.com, example.org/path)
    // If so, treat it as an external URL by prepending https://
    if (isBareDomain(href)) {
        return `https://${href}`;
    }
    // Normalize backslashes to forward slashes (as browsers do)
    let normalizedHref = href.replace(/\\/g, '/');
    // Collapse multiple leading slashes/backslashes to a single forward slash
    // This handles: //, ///, /\, \/\/, etc.
    normalizedHref = normalizedHref.replace(/^[/]+/, '/');
    // Convert relative paths starting with / to hash-based paths for in-app routing
    // This ensures the app's hash router handles the navigation
    if (normalizedHref.startsWith('/')) {
        normalizedHref = `#${normalizedHref}`;
    }
    return normalizedHref;
};
/**
 * Sanitizes anchor elements to prevent open redirect vulnerabilities and ensure secure links.
 *
 * This function:
 * 1. Normalizes the href to prevent open redirect attacks (e.g., /\google.com → #/google.com)
 * 2. Converts relative paths to hash-based paths for in-app routing
 * 3. Removes dangerous URL schemes (javascript:, data:, vbscript:)
 * 4. Adds rel="noopener noreferrer" for target="_blank" links
 *
 * @param node - The anchor element to sanitize
 */
export const sanitizeAnchorElement = (node) => {
    const href = node.getAttribute('href');
    if (href) {
        const sanitizedHref = sanitizeHrefUrl(href);
        if (sanitizedHref === null) {
            // Dangerous scheme - remove the href entirely
            node.removeAttribute('href');
        }
        else if (sanitizedHref !== href) {
            // URL was normalized - update the attribute
            node.setAttribute('href', sanitizedHref);
        }
    }
    // Add rel="noopener noreferrer" for target="_blank" links
    if (node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
    }
};
//# sourceMappingURL=sanitize-anchor.js.map